import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaSpinner, FaTimes } from 'react-icons/fa';
import {
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Typography,
    Fade,
    Alert
} from '@mui/material';
import {
    AccountCircle,
    Lock,
    LockReset,
    ArrowBack,
    Visibility,
    VisibilityOff
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import LoadingSpinner from "../utils/LoadingSpinner";
import UserService from '../services/UserService';

const ForgotPassword = () => {
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    // ── Étape courante : 1 = saisie username, 2 = saisie token + nouveau mdp ──
    const [step, setStep] = useState(1);

    // ── Étape 1 ──────────────────────────────────────────────────────────────
    const [username, setUsername] = useState('');
    const [loadingStep1, setLoadingStep1] = useState(false);
    const [errorStep1, setErrorStep1] = useState(null);
    const [errorStep1Visible, setErrorStep1Visible] = useState(false);

    // ── Étape 2 ──────────────────────────────────────────────────────────────
    const [token, setToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loadingStep2, setLoadingStep2] = useState(false);
    const [errorStep2, setErrorStep2] = useState(null);
    const [errorStep2Visible, setErrorStep2Visible] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState({});

    useEffect(() => {
        const timer = setTimeout(() => setIsLoading(false), 3000);
        return () => clearTimeout(timer);
    }, []);

    // ── Visibilité des alertes d'erreur ──────────────────────────────────────
    useEffect(() => {
        if (errorStep1) setTimeout(() => setErrorStep1Visible(true), 100);
        else setErrorStep1Visible(false);
    }, [errorStep1]);

    useEffect(() => {
        if (errorStep2) setTimeout(() => setErrorStep2Visible(true), 100);
        else setErrorStep2Visible(false);
    }, [errorStep2]);

    // ── Validation mot de passe (mêmes règles que RegisterPage) ─────────────
    const validatePasswords = (pwd, confirm) => {
        const errs = {};
        if (!pwd) {
            errs.newPassword = 'Le mot de passe est requis';
        } else {
            const rules = [
                { test: pwd.length >= 6,          msg: 'au moins 6 caractères' },
                { test: /[A-Z]/.test(pwd),        msg: 'une lettre majuscule' },
                { test: /[a-z]/.test(pwd),        msg: 'une lettre minuscule' },
                { test: /[0-9]/.test(pwd),        msg: 'un chiffre' },
                { test: /[^A-Za-z0-9]/.test(pwd), msg: 'un caractère spécial' },
            ];
            const failed = rules.filter(r => !r.test).map(r => r.msg);
            if (failed.length > 0) {
                errs.newPassword = `Le mot de passe doit contenir : ${failed.join(', ')}`;
            }
        }
        if (!confirm) {
            errs.confirmNewPassword = 'La confirmation est requise';
        } else if (confirm !== pwd) {
            errs.confirmNewPassword = 'Les mots de passe ne correspondent pas';
        }
        return errs;
    };

    // ── Étape 1 : envoi du token par mail ────────────────────────────────────
    const handleSendToken = async (e) => {
        e.preventDefault();
        if (!username.trim()) {
            setErrorStep1("Le nom d'utilisateur est requis");
            return;
        }

        setLoadingStep1(true);
        setErrorStep1(null);

        try {
            await UserService.sendPasswordResetToken({ username: username.toLowerCase() });

            await Swal.fire({
                icon: 'success',
                title: 'Token envoyé !',
                text: `Un token de réinitialisation a été envoyé à l'adresse mail associée au compte "${username.toLowerCase()}". Veuillez consulter votre boîte mail.`,
                confirmButtonText: 'Continuer',
                confirmButtonColor: '#1d8d36',
            });

            // L'utilisateur a cliqué sur OK → passage à l'étape 2
            setStep(2);
        } catch (err) {
            setErrorStep1(
                err.response?.data?.message ||
                "Nom d'utilisateur introuvable. Veuillez vérifier et réessayer."
            );
        } finally {
            setLoadingStep1(false);
        }
    };

    // ── Étape 2 : confirmation du token + nouveau mot de passe ───────────────
    const handleResetPassword = async (e) => {
        e.preventDefault();

        const errs = validatePasswords(newPassword, confirmNewPassword);
        if (!token.trim()) errs.token = 'Le token est requis';
        setPasswordErrors(errs);

        if (Object.keys(errs).length > 0) return;

        setLoadingStep2(true);
        setErrorStep2(null);

        try {
            await UserService.resetPassword({
                token,
                newPassword,
                confirmNewPassword,
            });

            await Swal.fire({
                icon: 'success',
                title: 'Mot de passe réinitialisé !',
                text: 'Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter.',
                confirmButtonText: 'Se connecter',
                confirmButtonColor: '#1d8d36',
            });

            navigate('/login');
        } catch (err) {
            setErrorStep2(
                err.response?.data?.message ||
                'Token invalide ou expiré. Veuillez recommencer la procédure.'
            );
        } finally {
            setLoadingStep2(false);
        }
    };

    // ── Styles partagés ──────────────────────────────────────────────────────
    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            '&:hover fieldset': { borderColor: '#153247' },
            '&.Mui-focused fieldset': { borderColor: '#153247' },
        },
        '& .MuiInputLabel-root.Mui-focused': { color: '#153247' },
    };

    const containerStyle = {
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        padding: '30px',
        borderRadius: '10px',
        width: '100%',
        maxWidth: '500px',
        margin: '20px auto',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s ease'
    };

    return (
        <>
            <LoadingSpinner isVisible={isLoading} loadingText="Chargement..." />

            <div style={{
                backgroundImage: `url('assets/images/banner/bg-register.jpg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <div style={containerStyle}>

                    {/* Logo */}
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <Link to="/">
                            <img
                                src='/assets/images/logo/logo.png'
                                alt="Logo"
                                style={{
                                    width: '100px',
                                    height: '100px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '3px solid rgb(23 50 71)'
                                }}
                                onError={(e) => { e.target.src = '/assets/images/logo/1.png'; }}
                            />
                        </Link>
                    </div>

                    <Typography variant="h4" style={{ textAlign: 'center', marginBottom: '8px' }}>
                        Mot de passe oublié
                    </Typography>
                    <Typography variant="body2" style={{ textAlign: 'center', marginBottom: '24px', color: '#666' }}>
                        {step === 1
                            ? "Saisissez votre nom d'utilisateur pour recevoir un token de réinitialisation par mail."
                            : "Saisissez le token reçu par mail ainsi que votre nouveau mot de passe."}
                    </Typography>

                    {/* ── ÉTAPE 1 ── */}
                    {step === 1 && (
                        <Fade in timeout={600}>
                            <form onSubmit={handleSendToken}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                                    <TextField
                                        fullWidth
                                        label={<>Nom d'utilisateur ou adresse mail <span style={{ color: 'red' }}>*</span></>}
                                        name="username"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value.toLowerCase())}
                                        disabled={loadingStep1}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AccountCircle sx={{ color: '#153247' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={fieldSx}
                                    />

                                    {errorStep1 && (
                                        <Alert
                                            severity="error"
                                            style={{
                                                opacity: errorStep1Visible ? 1 : 0,
                                                transition: 'opacity 0.3s ease-in-out'
                                            }}
                                            action={
                                                <IconButton
                                                    aria-label="close"
                                                    color="inherit"
                                                    size="small"
                                                    onClick={() => {
                                                        setErrorStep1Visible(false);
                                                        setTimeout(() => setErrorStep1(null), 300);
                                                    }}
                                                >
                                                    <FaTimes />
                                                </IconButton>
                                            }
                                        >
                                            {errorStep1}
                                        </Alert>
                                    )}

                                    <div style={{ textAlign: 'center' }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={loadingStep1 || !username.trim()}
                                            style={{
                                                backgroundColor: loadingStep1 || !username.trim()
                                                    ? 'rgba(29,141,54,0.58)'
                                                    : '#1d8d36',
                                                minWidth: '220px',
                                                borderRadius: '25px',
                                                transition: 'background-color 0.3s ease',
                                            }}
                                        >
                                            {loadingStep1 ? (
                                                <>
                                                    <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '18px' }} />
                                                    <span style={{ marginLeft: '8px' }}>Envoi en cours...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <LockReset style={{ marginRight: '8px' }} />
                                                    Réinitialiser le mot de passe
                                                </>
                                            )}
                                        </Button>
                                    </div>

                                    <div style={{ textAlign: 'center' }}>
                                        <Link to="/login" style={{
                                            color: '#1d8d36',
                                            textDecoration: 'none',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '5px'
                                        }}>
                                            <ArrowBack style={{ fontSize: '16px' }} />
                                            Retour à la connexion
                                        </Link>
                                    </div>
                                </div>
                            </form>
                        </Fade>
                    )}

                    {/* ── ÉTAPE 2 ── */}
                    {step === 2 && (
                        <Fade in timeout={600}>
                            <form onSubmit={handleResetPassword}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                                    {/* Token */}
                                    <TextField
                                        fullWidth
                                        label={<>Token reçu par mail <span style={{ color: 'red' }}>*</span></>}
                                        name="token"
                                        value={token}
                                        onChange={(e) => {
                                            setToken(e.target.value);
                                            setPasswordErrors(prev => ({ ...prev, token: undefined }));
                                        }}
                                        disabled={loadingStep2}
                                        error={!!passwordErrors.token}
                                        helperText={passwordErrors.token}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LockReset sx={{ color: '#153247' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={fieldSx}
                                    />

                                    {/* Nouveau mot de passe */}
                                    <TextField
                                        fullWidth
                                        label={<>Nouveau mot de passe <span style={{ color: 'red' }}>*</span></>}
                                        name="newPassword"
                                        type={showNewPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => {
                                            setNewPassword(e.target.value);
                                            setPasswordErrors(prev => ({
                                                ...prev,
                                                ...validatePasswords(e.target.value, confirmNewPassword)
                                            }));
                                        }}
                                        disabled={loadingStep2}
                                        error={!!passwordErrors.newPassword}
                                        helperText={passwordErrors.newPassword || 'Min. 6 caractères, majuscule, minuscule, chiffre, caractère spécial'}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock sx={{ color: '#153247' }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowNewPassword(p => !p)} edge="end">
                                                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={fieldSx}
                                    />

                                    {/* Confirmer le mot de passe */}
                                    <TextField
                                        fullWidth
                                        label={<>Confirmer le mot de passe <span style={{ color: 'red' }}>*</span></>}
                                        name="confirmNewPassword"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={confirmNewPassword}
                                        onChange={(e) => {
                                            setConfirmNewPassword(e.target.value);
                                            setPasswordErrors(prev => ({
                                                ...prev,
                                                confirmNewPassword: e.target.value !== newPassword
                                                    ? 'Les mots de passe ne correspondent pas'
                                                    : undefined
                                            }));
                                        }}
                                        disabled={loadingStep2}
                                        error={!!passwordErrors.confirmNewPassword}
                                        helperText={passwordErrors.confirmNewPassword}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock sx={{ color: '#153247' }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowConfirmPassword(p => !p)} edge="end">
                                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={fieldSx}
                                    />

                                    {errorStep2 && (
                                        <Alert
                                            severity="error"
                                            style={{
                                                opacity: errorStep2Visible ? 1 : 0,
                                                transition: 'opacity 0.3s ease-in-out'
                                            }}
                                            action={
                                                <IconButton
                                                    aria-label="close"
                                                    color="inherit"
                                                    size="small"
                                                    onClick={() => {
                                                        setErrorStep2Visible(false);
                                                        setTimeout(() => setErrorStep2(null), 300);
                                                    }}
                                                >
                                                    <FaTimes />
                                                </IconButton>
                                            }
                                        >
                                            {errorStep2}
                                        </Alert>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                                        {/* Retour étape 1 */}
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                setStep(1);
                                                setErrorStep2(null);
                                                setPasswordErrors({});
                                            }}
                                            startIcon={<ArrowBack />}
                                            style={{
                                                borderColor: '#ff6347',
                                                color: '#ff6347',
                                                borderRadius: '25px',
                                                minWidth: '120px',
                                            }}
                                        >
                                            Retour
                                        </Button>

                                        {/* Soumettre */}
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={
                                                loadingStep2 ||
                                                !token.trim() ||
                                                !newPassword ||
                                                !confirmNewPassword
                                            }
                                            style={{
                                                backgroundColor: (loadingStep2 || !token.trim() || !newPassword || !confirmNewPassword)
                                                    ? 'rgba(29,141,54,0.58)'
                                                    : '#1d8d36',
                                                minWidth: '180px',
                                                borderRadius: '25px',
                                                transition: 'background-color 0.3s ease',
                                            }}
                                        >
                                            {loadingStep2 ? (
                                                <>
                                                    <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '18px' }} />
                                                    <span style={{ marginLeft: '8px' }}>En cours...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <LockReset style={{ marginRight: '8px' }} />
                                                    Confirmer
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        </Fade>
                    )}
                </div>

                <style jsx>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        </>
    );
};

export default ForgotPassword;